import React, { useState } from "react";
import { X, Search, CalendarCheck, Clock, Check, XCircle, MessageCircle } from "lucide-react";
import { BRAND } from "../mock/mock";
import { trackBookings } from "../lib/api";
import { useBooking } from "../context/BookingContext";

const STATUS = {
  pending: { label: "Pending", color: "var(--viimii-gold)", bg: "rgba(176,134,63,0.15)", Icon: Clock },
  confirmed: { label: "Confirmed", color: "#3f9d63", bg: "rgba(127,201,154,0.2)", Icon: Check },
  completed: { label: "Completed", color: "var(--viimii-clay)", bg: "rgba(216,161,132,0.2)", Icon: Check },
  declined: { label: "Cancelled", color: "#c0584f", bg: "rgba(224,138,138,0.2)", Icon: XCircle },
};

export default function TrackBookingModal({ open, onClose }) {
  const { openBook } = useBooking();
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);

  if (!open) return null;

  const search = async (e) => {
    e.preventDefault();
    if (phone.replace(/\D/g, "").length < 6) return;
    setBusy(true);
    try {
      const data = await trackBookings(phone);
      setResults(data);
    } catch {
      setResults([]);
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    setPhone("");
    setResults(null);
    onClose();
  };

  const when = (b) => (b.date ? `${b.date}${b.time ? ` at ${b.time}` : ""}` : (b.timing || "-"));
  const inputCls =
    "w-full bg-viimii border hairline rounded-xl px-4 py-3.5 text-cream placeholder-cream/40 focus:outline-none focus:border-gold transition-colors";

  return (
    <div className="fixed inset-0 z-[80] grid place-items-center p-4" data-testid="track-booking-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={close} />
      <div className="relative w-full max-w-md bg-viimii-2 border hairline rounded-[28px] p-7 max-h-[90vh] overflow-y-auto">
        <button onClick={close} className="absolute top-5 right-5 text-cream/60 hover:text-cream" aria-label="Close"><X size={22} /></button>
        <p className="section-line text-gold flex items-center gap-2 mb-2"><CalendarCheck size={13} /> Track My Booking</p>
        <h3 className="font-display text-2xl text-cream mb-1">Check your appointment</h3>
        <p className="text-cream/55 text-sm mb-5">Enter the phone number you booked with to see your status.</p>

        <form onSubmit={search} className="flex gap-2 mb-5">
          <input
            className={inputCls}
            placeholder="Phone Number"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            data-testid="track-phone-input"
          />
          <button
            type="submit"
            disabled={busy}
            data-testid="track-search-button"
            className="shrink-0 inline-flex items-center gap-2 text-black font-medium px-5 rounded-xl hover:opacity-90 transition-opacity disabled:opacity-60"
            style={{ background: "var(--viimii-gold)" }}
          >
            <Search size={16} /> {busy ? "…" : "Find"}
          </button>
        </form>

        {results && results.length === 0 && (
          <div className="text-center py-6" data-testid="track-empty">
            <p className="text-cream/60 text-sm">No bookings found for this number.</p>
            <button
              onClick={() => { close(); openBook(); }}
              className="mt-4 inline-flex items-center gap-2 text-sm font-medium px-5 py-2.5 rounded-full border hairline text-cream/80 hover:text-gold"
            >
              Book an appointment
            </button>
          </div>
        )}

        {results && results.length > 0 && (
          <div className="space-y-3" data-testid="track-results">
            {results.map((b, i) => {
              const s = STATUS[b.status] || STATUS.pending;
              const S = s.Icon;
              return (
                <div key={i} data-testid="track-result-row" className="p-4 rounded-2xl border hairline bg-viimii">
                  <div className="flex items-center justify-between gap-3">
                    <h4 className="font-display text-lg text-cream">{b.service}</h4>
                    <span className="inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full" style={{ color: s.color, background: s.bg }}>
                      <S size={12} /> {s.label}
                    </span>
                  </div>
                  <p className="text-sm text-cream/65 mt-1.5 flex items-center gap-1.5">
                    <CalendarCheck size={14} style={{ color: "var(--viimii-gold)" }} /> {when(b)}
                  </p>
                  {b.status === "declined" && (
                    <button
                      onClick={() => { close(); openBook(); }}
                      data-testid="track-reschedule-button"
                      className="mt-3 inline-flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-full text-black hover:opacity-90 transition-opacity"
                      style={{ background: "var(--viimii-gold)" }}
                    >
                      Reschedule
                    </button>
                  )}
                </div>
              );
            })}
            <a
              href={`https://wa.me/${BRAND.whatsapp}`}
              target="_blank"
              rel="noreferrer"
              className="flex items-center justify-center gap-2 text-sm text-cream/60 hover:text-gold pt-1"
            >
              <MessageCircle size={14} /> Questions? Message us on WhatsApp
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
